import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import React from "react";
import { GET_PAYMENT_TOKENS } from "../../queries/GetPaymentTokens";
import { GET_PAYMENT_URL } from "../../queries/GetPaymentUrl";
import { DELETE_PAYMENT_TOKEN } from "../../mutations/DeletePaymentToken/DeletePaymentToken";
import {
  DeletePaymentTokenResponse,
  DeletePaymentTokenVariables,
} from "../../mutations/DeletePaymentToken/DeletePaymentToken.types";
import {
  GetPaymentUrlResponse,
  PaymentToken,
  PaymentTokensResponse,
} from "./BankCardsTypes";

export interface BankCardsActionResult {
  ok: boolean;
  message: string;
}

// Kept around for manual testing only — not used as a live fallback.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const MOCK_PAYMENT_TOKENS: PaymentToken[] = [
  {
    id: "mock-1",
    token: "mock-token-1",
    cardExpirationMonth: 12,
    cardExpirationYear: 2028,
    lastFourDigits: "5432",
    issuer: "MASTERCARD",
    expired: false,
  },
  {
    id: "mock-2",
    token: "mock-token-2",
    cardExpirationMonth: 3,
    cardExpirationYear: 2024,
    lastFourDigits: "1234",
    issuer: "Visa",
    expired: true,
  },
  {
    id: "mock-3",
    token: "mock-token-3",
    cardExpirationMonth: 7,
    cardExpirationYear: 2027,
    lastFourDigits: "9087",
    issuer: "American Express",
    expired: false,
  },
  {
    id: "mock-4",
    token: "mock-token-4",
    cardExpirationMonth: 5,
    cardExpirationYear: 2026,
    lastFourDigits: "6600",
    issuer: "unknown-issuer",
    expired: false,
  },
];

export const useBankCards = () => {
  const client = useApolloClient();

  const {
    data,
    loading: isLoading,
    error,
    refetch,
  } = useQuery<PaymentTokensResponse>(GET_PAYMENT_TOKENS, {
    fetchPolicy: "cache-and-network",
  });

  const [deletePaymentToken, { loading: isDeleting }] = useMutation<
    DeletePaymentTokenResponse,
    DeletePaymentTokenVariables
  >(DELETE_PAYMENT_TOKEN, {
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GET_PAYMENT_TOKENS }],
  });

  const [isAddingCard, setIsAddingCard] = React.useState(false);

  // If the user navigates to Cardcom and then hits "back", the browser can
  // restore this page from the bfcache instead of reloading it, leaving
  // isAddingCard frozen as true from right before the redirect.
  React.useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsAddingCard(false);
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const deleteCard = async (id: string): Promise<BankCardsActionResult> => {
    try {
      const res = await deletePaymentToken({ variables: { id } });

      if (res.data?.deletePaymentToken?.success) {
        return { ok: true, message: "" };
      }

      return { ok: false, message: "graphql" };
    } catch (e: any) {
      return { ok: false, message: e?.message ?? "network" };
    }
  };

  const addCard = async (): Promise<BankCardsActionResult> => {
    setIsAddingCard(true);

    try {
      const res = await client.query<GetPaymentUrlResponse>({
        query: GET_PAYMENT_URL,
        fetchPolicy: "network-only",
      });

      if (res.error || !res.data?.getPaymentUrl) {
        setIsAddingCard(false);
        return { ok: false, message: "graphql" };
      }

      setIsAddingCard(false);
      window.location.href = res.data.getPaymentUrl;
      return { ok: true, message: "" };
    } catch (e: any) {
      setIsAddingCard(false);
      return { ok: false, message: e?.message ?? "network" };
    }
  };

  return {
    items: data?.paymentTokens ?? [],
    isLoading,
    error,
    refetch,
    deleteCard,
    isDeleting,
    addCard,
    isAddingCard,
  };
};
